import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AVISO_DO_COMPUTADOR_COMPARTILHADO, entrarNaLembrada, esquecerLembrada, lembrarContaAtual, listarLembradas, noNavegador, sairDeTodasNoNavegador, USUARIO_DO_LOGIN, type ContaLembrada } from './contasDoNavegador';
import { EVENTO_DA_FOLHA } from './eventoDaFolha';
import { PAPEL, podeGuardar } from './regrasDasContas';
import { MenuDaConta } from './MenuDaConta';

/**
 * A FOLHA "TROCAR DE CONTA" NO NAVEGADOR (24/10/2026). A mesma folha do app (`FolhaDeContas`), com o que é do navegador:
 * - nada é guardado sozinho: a conta atual só é lembrada pela caixa do login ou pelo botão "Lembrar esta conta neste
 *   navegador" daqui, com o aviso do computador compartilhado;
 * - sem biometria: tocar numa conta lembrada entra nela; a que deixou de valer abre o login com o usuário preenchido;
 * - "Sair de todas as contas deste navegador": revoga todas e sai da sessão aberta.
 */
export function FolhaDeContasDoNavegador() {
  const { user, entrarComSessao, sairParaAdicionarConta, logout } = useAuth();
  const cliente = useQueryClient();
  const [aberta, setAberta] = useState(false);
  const [contas, setContas] = useState<ContaLembrada[]>([]);
  const [estado, setEstado] = useState<{ tom: 'info' | 'erro' | 'ok'; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState<number | 'lembrar' | 'todas' | null>(null);
  const [confirmarTodas, setConfirmarTodas] = useState(false);
  const atual = user ? Number(user.id) : null;
  const recarregar = useCallback(async () => { const l = await listarLembradas(); setContas(l); return l; }, []);

  const abrir = useCallback(async () => {
    if (!user || !noNavegador() || !podeGuardar(user)) return;
    setAberta(true); setConfirmarTodas(false);
    let erroAnterior: string | null = null;
    try { erroAnterior = sessionStorage.getItem('contas:erro'); sessionStorage.removeItem('contas:erro'); } catch { /* nada */ }
    setEstado(erroAnterior ? { tom: 'erro', texto: erroAnterior } : null);
    await recarregar();
  }, [user, recarregar]);
  useEffect(() => { const h = () => void abrir(); window.addEventListener(EVENTO_DA_FOLHA, h); return () => window.removeEventListener(EVENTO_DA_FOLHA, h); }, [abrir]);
  useEffect(() => { if (!user) setAberta(false); }, [user]);

  if (!aberta || !user) return null;
  const atualLembrada = contas.some((c) => c.userId === atual);
  const outras = contas.filter((c) => c.userId !== atual);
  const fechar = () => { setAberta(false); setConfirmarTodas(false); };

  const irAoLogin = (username: string) => {
    try { sessionStorage.setItem(USUARIO_DO_LOGIN, username); } catch { /* sem o usuário preenchido */ }
    setAberta(false); sairParaAdicionarConta();
  };
  const trocar = async (c: ContaLembrada) => {
    if (ocupado !== null) return;
    if (!c.valida) { irAoLogin(c.username); return; }
    setEstado(null); setOcupado(c.userId);
    try { const sessao = await entrarNaLembrada(c); cliente.clear(); setAberta(false); entrarComSessao(sessao); }   // sem limpar, a tela mostraria por um instante os dados da conta anterior
    catch (e) {
      const usuario = (e as { username?: string }).username;
      if (usuario) { irAoLogin(usuario); return; }
      setEstado({ tom: 'erro', texto: (e as Error).message });
    } finally { setOcupado(null); }
  };
  const lembrar = async () => {
    setOcupado('lembrar'); setEstado(null);
    try { await lembrarContaAtual(user); await recarregar(); setEstado({ tom: 'ok', texto: 'Esta conta fica lembrada neste navegador por 30 dias.' }); }
    catch (e) { setEstado({ tom: 'erro', texto: (e as Error).message }); }
    finally { setOcupado(null); }
  };
  const esquecer = async (c: ContaLembrada) => {
    setOcupado(c.userId); await esquecerLembrada(c.userId); await recarregar(); setOcupado(null);
    setEstado({ tom: 'ok', texto: c.userId === atual ? 'Esta conta não fica mais lembrada neste navegador. A sessão aberta continua.' : `${c.nome} não fica mais lembrada neste navegador.` });
  };
  const sairDeTodas = async () => {
    setOcupado('todas');
    try { await sairDeTodasNoNavegador(); } catch { /* sem rede: sai da sessão mesmo assim; as credenciais vencem em 30 dias */ }
    setOcupado(null); setAberta(false); cliente.clear(); logout();
  };

  const linha = (c: { userId: number; nome: string; username: string; papel: string }, eAtual: boolean, conta?: ContaLembrada) => (
    <li key={c.userId} data-testid={`conta-${c.userId}`} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 pb-1">
      <button type="button" disabled={eAtual || (ocupado !== null)} data-testid={`folha-conta-${c.userId}`} onClick={() => conta && void trocar(conta)}
        className="flex min-h-[56px] min-w-0 flex-1 items-center gap-3 py-2 text-left">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-marca-100 text-sm font-semibold text-marca-800" aria-hidden>{c.nome.trim().charAt(0).toUpperCase()}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-gray-900">{c.nome}</span>
          <span className="block truncate text-xs text-gray-500">
            {PAPEL[c.papel] ?? c.papel} · {c.username}
            {conta && !conta.valida && <span className="text-amber-700" data-testid={`pede-senha-${c.userId}`}> · entre com a senha</span>}
          </span>
        </span>
        {eAtual && <CheckIcon className="h-5 w-5 flex-shrink-0 text-marca-600" aria-label="conta atual" data-testid="conta-atual" />}
        {ocupado === c.userId && !eAtual && <span className="text-xs text-gray-500">Entrando…</span>}
      </button>
      {/* "Esquecer esta conta" em cada conta lembrada, também a aberta (24/10/2026): "Sair" já não esquece. */}
      {/* "Esquecer esta conta": no "⋯" de cada conta lembrada, também a aberta (`MenuDaConta`, 25/10/2026). "Sair" não esquece. */}
      {conta && <MenuDaConta userId={c.userId} nome={c.nome} onde="neste navegador" desabilitado={ocupado !== null} onEsquecer={() => esquecer(conta)} explicacao={'A conta continua existindo; para voltar a ela, será preciso a senha.'} />}
    </li>
  );

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 md:items-center" onClick={fechar} data-testid="folha-de-contas">
      <div className="w-full max-w-md rounded-t-2xl bg-white pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl md:rounded-2xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Trocar de conta">
        <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-300 md:hidden" aria-hidden />
        <div className="flex items-center justify-between px-4 pt-3">
          <h2 className="text-base font-semibold text-tinta">Trocar de conta</h2>
          <button type="button" onClick={fechar} className="min-h-[44px] px-2 text-sm text-gray-600">Fechar</button>
        </div>
        {estado && (
          <p role={estado.tom === 'erro' ? 'alert' : 'status'} data-testid="estado-da-folha"
            className={`mx-4 mt-1 rounded-md px-3 py-2 text-sm ${estado.tom === 'erro' ? 'bg-red-50 text-red-800' : estado.tom === 'ok' ? 'bg-green-50 text-green-800' : 'bg-marca-50 text-marca-800'}`}>{estado.texto}</p>
        )}
        <ul className="mt-2 max-h-[50vh] overflow-y-auto">
          {linha({ userId: Number(user.id), nome: user.name, username: user.username, papel: user.role }, true, contas.find((c) => c.userId === atual))}
          {outras.map((c) => linha(c, false, c))}
        </ul>
        {!atualLembrada && (
          <div className="mx-4 mt-1 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700" data-testid="lembrar-atual">
            Esta conta não está lembrada neste navegador: para voltar a ela depois de trocar, será preciso a senha.
            <button type="button" disabled={ocupado !== null} onClick={() => void lembrar()} className="mt-2 block min-h-[44px] w-full rounded-md border border-marca-300 bg-white px-3 text-sm font-medium text-marca-700" data-testid="lembrar-esta-conta">
              {ocupado === 'lembrar' ? 'Lembrando…' : 'Lembrar esta conta neste navegador'}
            </button>
            <span className="mt-1 block text-xs text-gray-500">{AVISO_DO_COMPUTADOR_COMPARTILHADO}</span>
          </div>
        )}
        <div className="mt-1 border-t border-gray-100 px-2 pt-1">
          {(
            <button type="button" disabled={ocupado !== null} onClick={() => { setAberta(false); sairParaAdicionarConta(); }}
              className="flex min-h-[52px] w-full items-center gap-3 rounded-md px-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50" data-testid="folha-adicionar">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300"><PlusIcon className="h-5 w-5" /></span>
              Adicionar conta
            </button>
          )}
          {contas.length > 0 && (confirmarTodas ? (
            <div className="mx-2 mb-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-gray-800" role="alertdialog" data-testid="confirmar-sair-de-todas">
              Sair de todas as contas deste navegador? Elas deixam de ser lembradas aqui, e a sessão aberta termina.
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" disabled={ocupado !== null} onClick={() => void sairDeTodas()} className="min-h-[44px] rounded-md bg-red-600 px-3 text-white" data-testid="sair-de-todas">Sair de todas</button>
                <button type="button" onClick={() => setConfirmarTodas(false)} className="min-h-[44px] rounded-md border border-gray-300 bg-white px-3">Cancelar</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmarTodas(true)} className="min-h-[44px] w-full px-2 text-left text-sm text-red-700" data-testid="abrir-sair-de-todas">
              Sair de todas as contas deste navegador
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
